import { BadgeCheck, CheckCircle, Clock, Info, Pencil, XCircle } from 'lucide-react'
import { capitalize } from 'lodash'
import { useMemo } from 'react'
import { ExpensesStatusEnum } from '../types/expenses'
import type { Expense } from '../types/expenses'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/utils/currencyUtils'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { formatDate } from '@/utils/timeZoneUtils'
import { useAuthStore } from '@/features/auth/store/authStore'

interface ExpenseCardProps {
  expense: Expense
  onDetail: (itemId: string) => void
}

export function ExpenseCard({ expense, onDetail }: ExpenseCardProps) {
  const user = useAuthStore((s) => s.user)
  const churches = useChurchesStore((s) => s.churches)

  const church = useMemo(
    () => churches.find((c) => c._id === expense.churchId),
    [churches, expense.churchId],
  )

  const expenseUsers = useMemo(
    () => [
      expense.localPastor?.userId,
      expense.adminTreasurer?.userId,
      expense.adminPastor?.userId,
    ],
    [
      expense.localPastor?.userId,
      expense.adminTreasurer?.userId,
      expense.adminPastor?.userId,
    ],
  )

  const isUserInvolved = user && expenseUsers.includes(user._id)

  return (
    <div key={expense._id} className="bg-white rounded-lg border p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="font-medium">{formatCurrency(expense.amount)}</div>
          <div className="text-sm text-neutral-600">
            {formatDate(expense.date, 'MMM D, YYYY')}
          </div>
        </div>
        <Badge
          color={
            expense.status === ExpensesStatusEnum.APPROVED
              ? 'green'
              : expense.status === ExpensesStatusEnum.CANCELLED
                ? 'red'
                : expense.status === ExpensesStatusEnum.UPDATING
                  ? 'blue'
                  : 'yellow'
          }
          icon={
            expense.status === ExpensesStatusEnum.APPROVED
              ? BadgeCheck
              : expense.status === ExpensesStatusEnum.CANCELLED
                ? XCircle
                : expense.status === ExpensesStatusEnum.UPDATING
                  ? Pencil
                  : Clock
          }
        >
          {expense.status === ExpensesStatusEnum.UPDATING
            ? 'Needs Correction'
            : capitalize(expense.status.toLowerCase())}
        </Badge>
      </div>

      <div className="space-y-2 text-sm">
        <div>
          <span className="text-neutral-600">Church:</span>{' '}
          {church?.name || 'Unknown'}
        </div>
        <div>
          <span className="text-neutral-600">Requester:</span>{' '}
          {expense.requester || 'Unknown'}
        </div>
        <div>
          <span className="text-neutral-600">Email:</span>{' '}
          {expense.requesterEmail || 'Unknown'}
        </div>
        {expense.notes && (
          <div>
            <span className="text-neutral-600">Notes:</span> {expense.notes}
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onDetail(expense._id)}
        >
          {expense.status === ExpensesStatusEnum.PENDING && isUserInvolved ? (
            <>
              <CheckCircle className="h-4 w-4" /> Review
            </>
          ) : (
            <>
              <Info className="h-4 w-4" /> Details
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
